/**
 * SKU Flavor Selector with Color Swatches
 *
 * Fetches and displays related products grouped by SKU prefix
 * Provides a variant-like selector with color swatches and taste labels
 *
 * Metafields used:
 * - custom.color_swatch (color): Background color for the swatch
 * - global.sortenbezeichnung_optional (single line text): Taste label below swatch
 */

class SKUFlavorSelector {
  constructor(element) {
    this.element = element;
    this.sku = element.dataset.sku;
    this.productId = element.dataset.productId;
    this.shopDomain = element.dataset.shopDomain;

    // Get current product's metafield data from data attributes
    this.currentColor = element.dataset.currentColor || "";
    this.currentTaste = element.dataset.currentTaste || "";
    this.currentImage = element.dataset.currentImage || "";
    this.displayStyle = element.dataset.displayStyle || "swatch";
    this.currentTags = element.dataset.currentTags
      ? element.dataset.currentTags.split(",").map(t => t.trim()).filter(Boolean)
      : [];
    this.currentUnitGrams = parseInt(element.dataset.currentUnitGrams, 10) || 0;

    this.loadingElement = element.querySelector(
      ".sku-flavor-selector__loading"
    );
    this.optionsElement = element.querySelector(
      ".sku-flavor-selector__options"
    );
    this.errorElement = element.querySelector(".sku-flavor-selector__error");
    this.errorText = element.querySelector(".sku-flavor-selector__error-text");
    this.currentFlavorElement = element.querySelector(
      ".sku-flavor-selector__current-flavor"
    );
    this.currentFlavorName = null;

    this.cache = this.getCache();

    this.init();
  }

  /**
   * Initialize the selector
   * Component stays collapsed until all data is ready to prevent layout shift
   */
  async init() {
    if (!this.sku) {
      console.warn("SKU Flavor Selector: No SKU available for this product");
      this.removeComponent();
      return;
    }

    console.log("SKU Flavor Selector: Initializing with SKU:", this.sku);
    console.log("SKU Flavor Selector: Shop domain:", this.shopDomain);

    try {
      const data = await this.fetchProducts();

      console.log("SKU Flavor Selector: API Response:", data);

      // Handle response format (API returns {products: []} directly)
      const products = data.products || [];
      const totalProducts = products.length;

      if (totalProducts === 0) {
        console.error(
          "SKU Flavor Selector: No products found for SKU:",
          this.sku
        );
        this.removeComponent();
        return;
      }

      if (totalProducts <= 1) {
        // Only one product, hide the selector entirely
        console.log(
          "SKU Flavor Selector: Only one product found, hiding selector"
        );
        this.removeComponent();
        return;
      }

      const currentProductId = this.productId.toString();

      // Extract base SKU from the first product's SKU
      const baseSKU = this.extractBaseSKU(this.sku);

      console.log("SKU Flavor Selector: Rendering", products.length, "products");

      // Inject current product's tags from Liquid (in case API doesn't return them)
      const productsWithTags = products.map((product) => {
        const productId = this.extractNumericId(product.id);
        if (productId === currentProductId && this.currentTags && this.currentTags.length > 0) {
          return { ...product, tags: this.currentTags };
        }
        return product;
      });

      // Filter by size tier using gram-based matching
      // Ensures Mini (10-13g), Klein (30g), and Regular (60-90g) products stay separated
      const sizeFilteredProducts = this.filterByUnitSize(productsWithTags, currentProductId);

      // Deduplicate products by taste label (same flavor, different package sizes)
      // Keep current product if it's a duplicate, otherwise keep first occurrence
      const deduplicatedProducts = this.deduplicateByTaste(sizeFilteredProducts, baseSKU, currentProductId);

      if (deduplicatedProducts.length <= 1) {
        console.log("SKU Flavor Selector: Only one unique flavor, hiding selector");
        this.removeComponent();
        return;
      }

      // Sort products: limitiert tagged items first
      const sortedProducts = this.sortProductsByLimitiert(deduplicatedProducts);

      // Render options while still collapsed
      this.renderOptions(sortedProducts, baseSKU);

      // Wait for next frame to ensure DOM is updated, then expand
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.expandComponent();
        });
      });
    } catch (error) {
      console.error("SKU Flavor Selector Error:", error);
      console.error("Error details:", error.message, error.stack);
      this.removeComponent();
    }
  }

  /**
   * Fetch products from the API with caching
   */
  async fetchProducts() {
    // Check cache first
    const cacheKey = `sku-${this.sku}`;
    const cached = this.cache[cacheKey];

    if (cached && this.isCacheValid(cached.timestamp)) {
      console.log("Using cached data for SKU:", this.sku);
      return cached.data;
    }

    // Fetch from API using GET (works better with Shopify App Proxy)
    const apiUrl = `https://${this.shopDomain}/apps/heimatgut-sku-flavor/products/by-sku`;
    const getUrl = `${apiUrl}?sku=${encodeURIComponent(this.sku)}`;

    console.log("SKU Flavor Selector: Fetching from API:", getUrl);

    try {
      const response = await fetch(getUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      console.log(
        "SKU Flavor Selector: Response status:",
        response.status,
        response.statusText
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("SKU Flavor Selector: Error response body:", errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("SKU Flavor Selector: Parsed response data:", data);

      // Cache the response
      this.setCache(cacheKey, data);

      return data;
    } catch (error) {
      console.error("SKU Flavor Selector: Request failed:", error);
      throw error;
    }
  }

  /**
   * Render product options as color swatches with taste labels
   * Limited editions get their own row at the top
   */
  renderOptions(products, baseSKU) {
    const currentProductId = this.productId.toString();

    // Separate limited and regular products
    const limitedProducts = [];
    const regularProducts = [];

    products.forEach((product) => {
      const productId = this.extractNumericId(product.id);
      const isCurrent = productId === currentProductId;
      const isLimited = isCurrent
        ? (this.currentTags && this.currentTags.some(tag => tag.toLowerCase() === 'limitiert'))
        : this.isLimitedEdition(product);

      if (isLimited) {
        limitedProducts.push(product);
      } else {
        regularProducts.push(product);
      }
    });

    // Render option HTML for a product based on display style
    const renderOption = (product) => {
      const productId = this.extractNumericId(product.id);
      const isCurrent = productId === currentProductId;
      const variant = product.variants && product.variants[0];

      const price = this.formatMoney(product.price_range.min);
      const compareAtPrice =
        variant && variant.compare_at_price
          ? this.formatMoney(variant.compare_at_price)
          : null;
      const hasDiscount =
        compareAtPrice &&
        parseFloat(variant.compare_at_price) >
          parseFloat(product.price_range.min);

      const flavorName = this.extractFlavorName(product.title, baseSKU);

      // Get color swatch, taste label, and image from metafields
      let colorSwatch = "";
      let tasteLabel = "";
      let productImage = "";

      if (isCurrent) {
        colorSwatch = this.currentColor;
        tasteLabel = this.currentTaste;
        productImage = this.currentImage;
      } else {
        colorSwatch = product.metafields?.custom?.color_swatch?.value ||
                      product.color_swatch ||
                      "";
        tasteLabel = product.metafields?.global?.sortenbezeichnung_optional?.value ||
                     product.taste_label ||
                     product.sortenbezeichnung_optional ||
                     "";
        productImage = product.featured_image || "";
      }

      if (!colorSwatch) {
        colorSwatch = this.getColorFromTitle(flavorName);
      }

      if (!tasteLabel) {
        tasteLabel = flavorName;
      }

      const classes = [
        "sku-flavor-selector__option",
        `sku-flavor-selector__option--${this.displayStyle}`,
        isCurrent ? "sku-flavor-selector__option--current" : "",
      ]
        .filter(Boolean)
        .join(" ");

      // Render based on display style
      let innerContent = "";

      if (this.displayStyle === "pill") {
        // Pill style - text only
        innerContent = `
          <span class="sku-flavor-selector__pill-label">${tasteLabel}</span>
          ${isCurrent ? `
            <span class="sku-flavor-selector__pill-check" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          ` : ""}
        `;
      } else if (this.displayStyle === "image") {
        // Image style
        innerContent = `
          <div class="sku-flavor-selector__image-wrapper">
            ${productImage ? `
              <img src="${this.optimizeImageUrl(productImage, 100)}" alt="${flavorName}" class="sku-flavor-selector__product-image" loading="lazy">
            ` : `
              <div class="sku-flavor-selector__image-placeholder">
                <span class="sku-flavor-selector__option-initial">${flavorName.charAt(0)}</span>
              </div>
            `}
            ${isCurrent ? `
              <span class="sku-flavor-selector__option-checkmark" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            ` : ""}
          </div>
          <span class="sku-flavor-selector__taste-label">${tasteLabel}</span>
        `;
      } else {
        // Default: swatch style
        innerContent = `
          <div class="sku-flavor-selector__swatch-wrapper">
            <div
              class="sku-flavor-selector__color-swatch"
              style="${colorSwatch ? `background-color: ${colorSwatch};` : ''}"
            >
              ${isCurrent ? `
                <span class="sku-flavor-selector__option-checkmark" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              ` : ""}
            </div>
            <span class="sku-flavor-selector__taste-label">${tasteLabel}</span>
          </div>
        `;
      }

      return `
        <a
          href="${isCurrent ? "#" : `/products/${product.handle}`}"
          class="${classes}"
          role="radio"
          aria-checked="${isCurrent}"
          aria-label="${product.title}${isCurrent ? " (current selection)" : ""}"
          ${isCurrent ? 'aria-current="page"' : ""}
          data-product-id="${productId}"
          data-sku="${variant ? variant.sku : ""}"
          data-flavor-name="${flavorName}"
          data-taste-label="${tasteLabel}"
          tabindex="0"
        >
          ${innerContent}
          <span class="sku-flavor-selector__sr-only">
            ${flavorName}. ${price}${hasDiscount ? `, was ${compareAtPrice}` : ""}. ${isCurrent ? "Currently viewing this flavor. " : ""}Click to view this flavor.
          </span>
        </a>
      `;
    };

    // Build the HTML
    let html = '';

    if (this.displayStyle === 'dropdown') {
      // Dropdown mode — bottom-sheet style with product images
      const allProducts = [...limitedProducts, ...regularProducts];
      const totalCount = allProducts.length;
      const currentProduct = allProducts.find(p => this.extractNumericId(p.id) === currentProductId);
      const currentFlavor = currentProduct ? this.extractFlavorName(currentProduct.title, baseSKU) : '';
      const currentTaste = this.currentTaste || currentFlavor;
      const currentImg = this.currentImage || (currentProduct?.featured_image) || '';

      const renderDropdownOption = (product, isLimited) => {
        const productId = this.extractNumericId(product.id);
        const isCurrent = productId === currentProductId;
        const flavorName = this.extractFlavorName(product.title, baseSKU);
        let tasteLabel = '';
        let productImage = '';

        if (isCurrent) {
          tasteLabel = this.currentTaste || flavorName;
          productImage = this.currentImage || '';
        } else {
          tasteLabel = product.metafields?.global?.sortenbezeichnung_optional?.value ||
                       product.taste_label ||
                       product.sortenbezeichnung_optional ||
                       flavorName;
          productImage = product.featured_image || '';
        }

        return `
          <button
            class="sku-flavor-selector__dropdown-item${isCurrent ? ' sku-flavor-selector__dropdown-item--current' : ''}"
            data-handle="${product.handle}"
            data-product-id="${productId}"
            data-flavor-name="${flavorName}"
            data-taste-label="${tasteLabel}"
            role="option"
            aria-selected="${isCurrent}"
            type="button"
          >
            <span class="sku-flavor-selector__dropdown-img">
              ${productImage ? `<img src="${this.optimizeImageUrl(productImage, 120)}" alt="${tasteLabel}" loading="lazy">` : `<span class="sku-flavor-selector__dropdown-img-placeholder">${tasteLabel.charAt(0)}</span>`}
            </span>
            <span class="sku-flavor-selector__dropdown-text">
              <span class="sku-flavor-selector__dropdown-label">${tasteLabel}</span>
              ${isLimited ? '<span class="sku-flavor-selector__dropdown-limited-tag">LIMITED</span>' : ''}
            </span>
            ${isCurrent ? '<svg class="sku-flavor-selector__dropdown-check" width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}
          </button>
        `;
      };

      html = `
        <div class="sku-flavor-selector__dropdown">
          <button class="sku-flavor-selector__dropdown-trigger" type="button" aria-haspopup="listbox" aria-expanded="false">
            <span class="sku-flavor-selector__dropdown-img">
              ${currentImg ? `<img src="${this.optimizeImageUrl(currentImg, 80)}" alt="${currentTaste}">` : ''}
            </span>
            <span class="sku-flavor-selector__dropdown-text">
              <span class="sku-flavor-selector__dropdown-value">${currentTaste}</span>
              <span class="sku-flavor-selector__dropdown-count">(${totalCount} Sorten)</span>
            </span>
            <svg class="sku-flavor-selector__dropdown-arrow" width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <div class="sku-flavor-selector__dropdown-overlay"></div>
          <div class="sku-flavor-selector__dropdown-menu" role="listbox">
            <div class="sku-flavor-selector__dropdown-menu-header">
              <span class="sku-flavor-selector__dropdown-menu-title">Sorte wählen</span>
              <button class="sku-flavor-selector__dropdown-close" type="button" aria-label="Schließen">
                <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              </button>
            </div>
            <div class="sku-flavor-selector__dropdown-menu-items">
              ${limitedProducts.map(p => renderDropdownOption(p, true)).join('')}
              ${regularProducts.map(p => renderDropdownOption(p, false)).join('')}
            </div>
          </div>
        </div>
      `;

      this.optionsElement.innerHTML = html;

      // Setup dropdown behavior
      this.setupDropdown();
    } else {
      // Swatch / Image / Pill modes — render rows
      // Limited edition row (if any exist)
      if (limitedProducts.length > 0) {
        html += `
          <div class="sku-flavor-selector__row sku-flavor-selector__row--limited">
            <span class="sku-flavor-selector__row-label">Limitiert</span>
            <div class="sku-flavor-selector__row-swatches">
              ${limitedProducts.map(renderOption).join('')}
            </div>
          </div>
        `;
      }

      // Regular products row
      if (regularProducts.length > 0) {
        html += `
          <div class="sku-flavor-selector__row sku-flavor-selector__row--regular">
            <div class="sku-flavor-selector__row-swatches">
              ${regularProducts.map(renderOption).join('')}
            </div>
          </div>
        `;
      }

      this.optionsElement.innerHTML = html;
    }

    // Initialize current flavor name display
    this.initializeFlavorNameDisplay(products, baseSKU);

    // Add keyboard navigation
    this.setupKeyboardNavigation();

    // Add hover event listeners for flavor name display
    this.setupFlavorNameHover();
  }

  /**
   * Get a fallback color based on flavor name
   */
  getColorFromTitle(flavorName) {
    const lowerName = flavorName.toLowerCase();

    // Common flavor-to-color mappings (German and English)
    const colorMap = {
      // Chocolate/Cocoa
      'schokolade': '#5D3A1A',
      'chocolate': '#5D3A1A',
      'schoko': '#5D3A1A',
      'kakao': '#4A2C2A',
      'cocoa': '#4A2C2A',
      'dark': '#2C1810',
      'dunkel': '#2C1810',

      // Caramel
      'karamell': '#C68642',
      'caramel': '#C68642',

      // Vanilla
      'vanille': '#F3E5AB',
      'vanilla': '#F3E5AB',

      // Strawberry
      'erdbeere': '#FC5A8D',
      'strawberry': '#FC5A8D',

      // Blueberry
      'blaubeere': '#4F86F7',
      'blueberry': '#4F86F7',
      'heidelbeere': '#4F86F7',

      // Apple
      'apfel': '#8DB600',
      'apple': '#8DB600',

      // Orange
      'orange': '#FF8C00',

      // Lemon
      'zitrone': '#FFF44F',
      'lemon': '#FFF44F',

      // Mint
      'minze': '#98FF98',
      'mint': '#98FF98',

      // Peanut
      'erdnuss': '#C19A6B',
      'peanut': '#C19A6B',

      // Coconut
      'kokos': '#FFFAF0',
      'coconut': '#FFFAF0',

      // Hazelnut
      'haselnuss': '#8B4513',
      'hazelnut': '#8B4513',

      // Coffee
      'kaffee': '#6F4E37',
      'coffee': '#6F4E37',

      // Cinnamon
      'zimt': '#D2691E',
      'cinnamon': '#D2691E',
    };

    // Check if any keyword matches
    for (const [keyword, color] of Object.entries(colorMap)) {
      if (lowerName.includes(keyword)) {
        return color;
      }
    }

    // Default fallback color
    return '#E8E0D8';
  }

  /**
   * Initialize the current flavor name display
   */
  initializeFlavorNameDisplay(products, baseSKU) {
    if (!this.currentFlavorElement) return;

    const currentProductId = this.productId.toString();
    const currentProduct = products.find(
      (product) =>
        this.extractNumericId(product.id).toString() === currentProductId
    );

    if (currentProduct) {
      this.currentFlavorName = this.extractFlavorName(
        currentProduct.title,
        baseSKU
      );
      this.currentFlavorElement.textContent = this.currentFlavorName;
    }
  }

  /**
   * Setup hover event listeners to update flavor name display
   */
  setupFlavorNameHover() {
    if (!this.currentFlavorElement) return;

    const options = Array.from(
      this.optionsElement.querySelectorAll(".sku-flavor-selector__option")
    );

    options.forEach((option) => {
      const flavorName = option.dataset.flavorName;

      if (!flavorName) return;

      // Only handle hover on desktop (not touch devices)
      if (window.matchMedia("(hover: hover)").matches) {
        option.addEventListener("mouseenter", () => {
          this.currentFlavorElement.textContent = flavorName;
        });

        option.addEventListener("mouseleave", () => {
          if (this.currentFlavorName) {
            this.currentFlavorElement.textContent = this.currentFlavorName;
          }
        });

        // Also handle focus for keyboard navigation
        option.addEventListener("focus", () => {
          this.currentFlavorElement.textContent = flavorName;
        });

        option.addEventListener("blur", () => {
          if (this.currentFlavorName) {
            this.currentFlavorElement.textContent = this.currentFlavorName;
          }
        });
      }
    });
  }

  /**
   * Setup dropdown toggle, item selection, and outside-click closing
   */
  setupDropdown() {
    const dropdown = this.element.querySelector('.sku-flavor-selector__dropdown');
    const trigger = dropdown.querySelector('.sku-flavor-selector__dropdown-trigger');
    const menu = dropdown.querySelector('.sku-flavor-selector__dropdown-menu');
    const overlay = dropdown.querySelector('.sku-flavor-selector__dropdown-overlay');
    const closeBtn = dropdown.querySelector('.sku-flavor-selector__dropdown-close');
    const items = Array.from(menu.querySelectorAll('.sku-flavor-selector__dropdown-item'));
    let isOpen = false;

    // Portal overlay + menu to <body> so they escape any parent
    // stacking context, overflow:hidden, or transforms.
    document.body.appendChild(overlay);
    document.body.appendChild(menu);

    const isDesktop = () => window.matchMedia('(min-width: 750px)').matches;

    // Reposition the desktop dropdown menu relative to the trigger
    const positionMenu = () => {
      const rect = trigger.getBoundingClientRect();
      menu.style.top = `${rect.bottom + 4}px`;
      menu.style.left = `${rect.left}px`;
      menu.style.width = `${rect.width}px`;
    };

    // Scroll/resize handler: reposition on desktop, close if trigger is out of view
    const onScrollOrResize = () => {
      if (!isOpen || !isDesktop()) return;
      const rect = trigger.getBoundingClientRect();
      // Close the menu if the trigger has scrolled out of the viewport
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        closeMenu();
        return;
      }
      positionMenu();
    };

    const openMenu = () => {
      isOpen = true;
      trigger.setAttribute('aria-expanded', 'true');

      if (isDesktop()) {
        positionMenu();
        // Keep the menu anchored to the trigger while scrolling/resizing
        window.addEventListener('scroll', onScrollOrResize, { passive: true });
        window.addEventListener('resize', onScrollOrResize, { passive: true });
      } else {
        // Mobile bottom-sheet: reset any inline desktop positioning
        menu.style.top = '';
        menu.style.left = '';
        menu.style.width = '';
        document.body.style.overflow = 'hidden';
        // Hide sticky ATC so it doesn't sit on top of the bottom sheet
        document.querySelectorAll('.lumin-sticky-atc, .sticky-atc').forEach(
          el => el.style.setProperty('display', 'none', 'important')
        );
        // Show overlay, then animate in
        overlay.classList.add('sku-flavor-selector__dropdown-overlay--visible');
        overlay.offsetHeight;
        overlay.classList.add('sku-flavor-selector__dropdown-overlay--open');
      }

      menu.classList.add('sku-flavor-selector__dropdown-menu--open');
    };

    const closeMenu = () => {
      if (!isOpen) return;
      isOpen = false;
      menu.classList.remove('sku-flavor-selector__dropdown-menu--open');
      trigger.setAttribute('aria-expanded', 'false');

      // Always clean up desktop scroll/resize listeners
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);

      if (!isDesktop()) {
        overlay.classList.remove('sku-flavor-selector__dropdown-overlay--open');
        document.body.style.overflow = '';
        // Restore sticky ATC
        document.querySelectorAll('.lumin-sticky-atc, .sticky-atc').forEach(
          el => el.style.removeProperty('display')
        );
        // Remove overlay pointer-events after transition ends
        const onEnd = () => {
          if (!isOpen) overlay.classList.remove('sku-flavor-selector__dropdown-overlay--visible');
          overlay.removeEventListener('transitionend', onEnd);
        };
        overlay.addEventListener('transitionend', onEnd);
      }
    };

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      isOpen ? closeMenu() : openMenu();
    });

    overlay.addEventListener('click', closeMenu);
    closeBtn.addEventListener('click', closeMenu);

    // Close desktop dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!isOpen || !isDesktop()) return;
      if (!menu.contains(e.target) && !trigger.contains(e.target)) {
        closeMenu();
      }
    });

    items.forEach((item) => {
      item.addEventListener('click', () => {
        const handle = item.dataset.handle;
        const isCurrent = item.classList.contains('sku-flavor-selector__dropdown-item--current');
        if (!isCurrent && handle) {
          window.location.href = `/products/${handle}`;
        }
        closeMenu();
      });
    });

    menu.addEventListener('keydown', (e) => {
      const focusable = items.filter(i => !i.hidden);
      const idx = focusable.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        focusable[(idx + 1) % focusable.length]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        focusable[(idx - 1 + focusable.length) % focusable.length]?.focus();
      } else if (e.key === 'Escape') {
        closeMenu();
        trigger.focus();
      }
    });
  }

  /**
   * Extract base SKU prefix from full SKU
   * Example: "POP-203" -> "POP"
   */
  extractBaseSKU(sku) {
    if (!sku) return "";
    // Split by common separators
    const separators = ["-", "_", " "];
    for (const sep of separators) {
      if (sku.includes(sep)) {
        return sku.split(sep)[0];
      }
    }
    // If no separator, return first 3 characters
    return sku.substring(0, 3);
  }

  /**
   * Extract per-unit gram weight from a variant option value string.
   * Handles patterns like "10 x 90g", "1 x 13g", "125g".
   * Returns 0 if no gram value can be parsed.
   */
  extractUnitGrams(optionValue) {
    if (!optionValue) return 0;

    // Pattern: "{count} x {grams}g"
    const multipackMatch = optionValue.match(/(\d+)\s*x\s*(\d+)\s*g/i);
    if (multipackMatch) {
      return parseInt(multipackMatch[2], 10);
    }

    // Pattern: "{grams}g" (standalone)
    const simpleMatch = optionValue.match(/(\d+)\s*g/i);
    if (simpleMatch) {
      return parseInt(simpleMatch[1], 10);
    }

    return 0;
  }

  /**
   * Determine the size tier for a product based on available signals.
   * Uses gram value (most reliable), then title keywords, then tags as fallbacks.
   *
   * Tiers:
   *   - 'mini':    per-unit <= 15g  (e.g., 10g, 13g Mini products)
   *   - 'klein':   per-unit 16-45g  (e.g., 30g, 40g klein/small products)
   *   - 'regular': per-unit > 45g   (e.g., 60g, 75g, 90g, 125g standard products)
   *
   * @param {number} unitGrams - Per-unit gram weight (0 if unknown)
   * @param {string} title - Product title
   * @param {string|string[]} tags - Product tags (comma-separated string or array)
   * @returns {'mini'|'klein'|'regular'}
   */
  determineSizeTier(unitGrams, title, tags) {
    // 1. Gram-based (most reliable signal)
    if (unitGrams > 0) {
      if (unitGrams <= 15) return 'mini';
      if (unitGrams <= 45) return 'klein';
      return 'regular';
    }

    // 2. Title keyword fallback
    const lowerTitle = (title || '').toLowerCase();
    if (lowerTitle.includes('mini')) return 'mini';
    if (lowerTitle.includes('klein')) return 'klein';

    // 3. Tag fallback
    const tagArray = Array.isArray(tags)
      ? tags
      : (tags || '').split(',').map(t => t.trim()).filter(Boolean);
    const lowerTags = tagArray.map(t => t.toLowerCase());

    if (lowerTags.includes('mini')) return 'mini';
    if (lowerTags.includes('small')) return 'klein';

    // Default: regular
    return 'regular';
  }

  /**
   * Extract flavor name from product title
   * Attempts to extract the distinctive part of the title
   */
  extractFlavorName(title, baseSKU) {
    // Common separators to try
    const separators = [" - ", " – ", " — ", ": ", " | "];

    for (const separator of separators) {
      if (title.includes(separator)) {
        const parts = title.split(separator);
        // Return the last part (usually the flavor/variant name)
        return parts[parts.length - 1].trim();
      }
    }

    // If no separator found, return the full title
    return title;
  }

  /**
   * Filter products by size tier using gram-based matching.
   *
   * Determines the current product's size tier from its per-unit gram weight
   * (passed from Liquid via data attribute). For API products, determines tier
   * from title keywords and tags since the API may not return option values.
   *
   * Only shows products in the same size tier as the current product.
   * e.g., viewing a 90g regular popcorn won't show 13g mini popcorn.
   */
  filterByUnitSize(products, currentProductId) {
    // Determine current product's size tier
    const currentTier = this.determineSizeTier(
      this.currentUnitGrams,
      '', // title not needed when we have grams
      this.currentTags
    );

    console.log(
      `SKU Flavor Selector: Current product size tier: "${currentTier}" (${this.currentUnitGrams}g)`
    );

    return products.filter((product) => {
      // Always include current product
      if (this.extractNumericId(product.id) === currentProductId) {
        return true;
      }

      // For API products: try to extract grams from variant option values
      let productGrams = 0;
      if (product.variants && product.variants.length > 0) {
        const variant = product.variants[0];
        // Check if API returns selectedOptions or option values
        if (variant.selectedOptions) {
          const sizeOption = variant.selectedOptions.find(opt =>
            ['size', 'größe', 'menge', 'packung'].includes(opt.name.toLowerCase())
          );
          if (sizeOption) {
            productGrams = this.extractUnitGrams(sizeOption.value);
          }
        }
        // Also check option1/option2/option3 format (Storefront API)
        if (productGrams === 0 && variant.option1) {
          productGrams = this.extractUnitGrams(variant.option1);
        }
      }

      // Determine product's size tier using all available signals
      const tags = product.tags || [];
      const productTier = this.determineSizeTier(productGrams, product.title, tags);

      const included = productTier === currentTier;
      if (!included) {
        console.log(
          `SKU Flavor Selector: Filtered out "${product.title}" — tier "${productTier}" != current "${currentTier}"`
        );
      }

      return included;
    });
  }

  /**
   * Deduplicate products by taste label
   * Same flavor with different package sizes should only show once
   * Prefers the current product if it's a duplicate, otherwise keeps first occurrence
   */
  deduplicateByTaste(products, baseSKU, currentProductId) {
    const seen = new Map(); // tasteLabel -> product

    for (const product of products) {
      const productId = this.extractNumericId(product.id);
      const isCurrent = productId === currentProductId;

      // Get taste label for this product
      let tasteLabel = "";
      if (isCurrent) {
        tasteLabel = this.currentTaste;
      } else {
        tasteLabel = product.metafields?.global?.sortenbezeichnung_optional?.value ||
                     product.taste_label ||
                     product.sortenbezeichnung_optional ||
                     "";
      }

      // Fallback to extracted flavor name if no taste label
      if (!tasteLabel) {
        tasteLabel = this.extractFlavorName(product.title, baseSKU);
      }

      // Normalize for comparison (lowercase, trimmed)
      const normalizedLabel = tasteLabel.toLowerCase().trim();

      // If we haven't seen this taste, or this is the current product, use it
      if (!seen.has(normalizedLabel) || isCurrent) {
        seen.set(normalizedLabel, product);
      }
    }

    return Array.from(seen.values());
  }

  /**
   * Check if product has the "limitiert" tag (case-insensitive)
   */
  isLimitedEdition(product) {
    if (!product.tags) {
      return false;
    }

    // Handle both array and comma-separated string formats
    const tags = Array.isArray(product.tags)
      ? product.tags
      : product.tags.split(',').map(t => t.trim());

    return tags.some(tag =>
      tag.toLowerCase() === 'limitiert'
    );
  }

  /**
   * Sort products with "limitiert" tag first
   */
  sortProductsByLimitiert(products) {
    return [...products].sort((a, b) => {
      const aIsLimited = this.isLimitedEdition(a);
      const bIsLimited = this.isLimitedEdition(b);

      if (aIsLimited && !bIsLimited) return -1;
      if (!aIsLimited && bIsLimited) return 1;
      return 0; // maintain original order for same category
    });
  }

  /**
   * Extract numeric ID from Shopify GID
   */
  extractNumericId(gid) {
    if (!gid) return null;
    const match = gid.match(/\/(\d+)$/);
    return match ? match[1] : gid.toString();
  }

  /**
   * Optimize Shopify image URL for specific width
   */
  optimizeImageUrl(url, width) {
    if (!url) return "";

    // Remove existing size parameter if present
    const cleanUrl = url.replace(
      /_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i,
      ".$2$3"
    );

    // Add new size parameter
    return cleanUrl.replace(
      /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i,
      `_${width}x.$1$2`
    );
  }

  /**
   * Format money according to Shopify's format
   */
  formatMoney(amount) {
    if (typeof amount === "string") {
      amount = parseFloat(amount);
    }

    // Check if window.Shopify.formatMoney is available (from lumin-main.js)
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      const cents = Math.round(amount * 100);
      return window.Shopify.formatMoney(cents, window.lumintheme?.moneyFormat);
    }

    // Fallback formatting
    const formatted = amount.toFixed(2);

    // Try to get currency symbol from shop configuration
    const currency = this.getCurrencySymbol();
    return `${currency}${formatted}`;
  }

  /**
   * Get currency symbol (fallback method)
   */
  getCurrencySymbol() {
    // Try to detect from existing price elements on page
    const priceElement = document.querySelector('[class*="price"]');
    if (priceElement) {
      const priceText = priceElement.textContent;
      const symbols = ["$", "€", "£", "¥", "kr", "CHF"];
      for (const symbol of symbols) {
        if (priceText.includes(symbol)) {
          return symbol;
        }
      }
    }
    return "$"; // Default fallback
  }

  /**
   * Setup keyboard navigation for accessibility
   */
  setupKeyboardNavigation() {
    const options = Array.from(
      this.optionsElement.querySelectorAll(".sku-flavor-selector__option")
    );

    options.forEach((option, index) => {
      option.addEventListener("keydown", (e) => {
        let targetIndex;

        switch (e.key) {
          case "ArrowRight":
          case "ArrowDown":
            e.preventDefault();
            targetIndex = (index + 1) % options.length;
            options[targetIndex].focus();
            break;

          case "ArrowLeft":
          case "ArrowUp":
            e.preventDefault();
            targetIndex = (index - 1 + options.length) % options.length;
            options[targetIndex].focus();
            break;

          case "Home":
            e.preventDefault();
            options[0].focus();
            break;

          case "End":
            e.preventDefault();
            options[options.length - 1].focus();
            break;

          case "Enter":
          case " ":
            e.preventDefault();
            if (
              !option.classList.contains(
                "sku-flavor-selector__option--current"
              ) &&
              !option.classList.contains(
                "sku-flavor-selector__option--unavailable"
              )
            ) {
              window.location.href = option.href;
            }
            break;
        }
      });
    });
  }

  /**
   * Cache management
   */
  getCache() {
    try {
      const cached = localStorage.getItem("sku-flavor-selector-cache");
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      console.warn("Failed to read cache:", error);
      return {};
    }
  }

  setCache(key, data) {
    try {
      this.cache[key] = {
        data: data,
        timestamp: Date.now(),
      };
      localStorage.setItem(
        "sku-flavor-selector-cache",
        JSON.stringify(this.cache)
      );
    } catch (error) {
      console.warn("Failed to write cache:", error);
    }
  }

  isCacheValid(timestamp) {
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    return Date.now() - timestamp < CACHE_DURATION;
  }

  /**
   * UI State Management - Layout Shift Prevention
   *
   * Strategy:
   * - Component starts collapsed (height: 0)
   * - Content is rendered while collapsed
   * - expandComponent() smoothly animates to natural height
   * - removeComponent() removes from DOM without causing shift
   */

  /**
   * Expand the component smoothly after content is ready
   * Uses CSS transitions for smooth height animation
   */
  expandComponent() {
    if (!this.element) return;

    // Get the natural height of the content
    const inner = this.element.querySelector('.sku-flavor-selector__inner');
    if (!inner) return;

    // Temporarily make visible to measure
    this.element.style.height = 'auto';
    const naturalHeight = this.element.offsetHeight;

    // Reset to 0 for animation
    this.element.style.height = '0px';

    // Force reflow
    this.element.offsetHeight;

    // Animate to natural height
    this.element.style.height = `${naturalHeight}px`;

    // Switch classes for the transition
    this.element.classList.remove('sku-flavor-selector--collapsed');
    this.element.classList.add('sku-flavor-selector--expanded');
    this.element.setAttribute('aria-hidden', 'false');

    // After transition, set height to auto for responsive behavior
    const onTransitionEnd = () => {
      this.element.style.height = 'auto';
      this.element.removeEventListener('transitionend', onTransitionEnd);
    };
    this.element.addEventListener('transitionend', onTransitionEnd);

    console.log("SKU Flavor Selector: Component expanded");
  }

  /**
   * Remove the component from DOM entirely
   * Called when there's no data or only one product
   * No layout shift since component was never visible
   */
  removeComponent() {
    if (this.element) {
      // Remove from DOM - no layout shift since it was collapsed
      this.element.remove();
      console.log("SKU Flavor Selector: Component removed (no shift)");
    }
  }

  /**
   * @deprecated Use removeComponent() instead
   */
  hideComponent() {
    this.removeComponent();
  }
}

/**
 * Initialize all SKU Flavor Selectors on the page
 */
function initSKUFlavorSelectors() {
  const selectors = document.querySelectorAll(".sku-flavor-selector");

  selectors.forEach((element) => {
    new SKUFlavorSelector(element);
  });
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSKUFlavorSelectors);
} else {
  initSKUFlavorSelectors();
}

// Re-initialize on dynamic content load (for AJAX-loaded product pages)
document.addEventListener("shopify:section:load", initSKUFlavorSelectors);
